import React from "react";

import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from "@ant-design/icons";
import { Spin } from "antd";

import buildingFillIcon from "../../../../../assets/images/subscriptions/building-fill.svg";
import flaskOrangeWatermark from "../../../../../assets/images/subscriptions/flask-orange.svg";
import flaskRedWatermark from "../../../../../assets/images/subscriptions/flask-red.svg";
import flaskWatermark from "../../../../../assets/images/subscriptions/flask.svg";
import lineChartIcon from "../../../../../assets/images/subscriptions/line-chart-line.svg";

import "./MultiLocationSummaryCards.css";

const formatTests = (value) => {
  const parsed = Number(value);
  const amount = Number.isFinite(parsed) ? Math.floor(parsed) : 0;
  const formatted = amount.toLocaleString();
  const unit = amount === 1 ? "test" : "tests";
  return `${formatted} ${unit}`;
};

const GROWTH_STATUS_TONE = {
  positive: "warning",
  negative: "negative",
  muted: "muted",
};

const getPoolStatus = (poolState, runningLowCount) => {
  if (poolState === "empty") {
    return {
      icon: <ExclamationCircleFilled />,
      label: "No tests left",
      tone: "negative",
      valueTone: "negative",
      watermark: flaskRedWatermark,
    };
  }

  if (poolState === "low" || poolState === "lowSingle") {
    const countLabel = runningLowCount === 1 ? "category" : "categories";
    return {
      icon: <InfoCircleFilled />,
      label: `${runningLowCount} ${countLabel} running low`,
      tone: "warning",
      valueTone: poolState === "lowSingle" ? "positive" : "warning",
      watermark: poolState === "lowSingle" ? flaskWatermark : flaskOrangeWatermark,
    };
  }

  return {
    icon: <CheckCircleFilled />,
    label: "Ready for submission",
    tone: "positive",
    valueTone: "positive",
    watermark: flaskWatermark,
  };
};

export default function MultiLocationSummaryCards({
  sharedPoolAvailable,
  poolState = "ready",
  runningLowCount = 0,
  consumedSummary = null,
  consumedSummaryReady = false,
  periodLabel,
  activeLocations,
  activeLocationsStatus = "All locations onboarded",
  selectedLocationLabel = "",
}) {
  const poolStatus = getPoolStatus(poolState, runningLowCount);
  const isLocationSelected = Boolean(selectedLocationLabel);
  const sharedPoolStatusLabel = isLocationSelected && poolState === "ready"
    ? "All categories healthy"
    : poolStatus.label;
  const growthTone = GROWTH_STATUS_TONE[consumedSummary?.detailTone] || "muted";
  let consumedTestsValue = "";
  if (consumedSummaryReady) {
    consumedTestsValue = consumedSummary?.count == null
      ? "Not enough data"
      : consumedSummary.value;
  }

  return (
    <section className="MultiLocationSummaryCards">
      <article className={`MultiLocationSummaryCards__Card MultiLocationSummaryCards__Card--withWatermark MultiLocationSummaryCards__Card--${poolState}`}>
        <img
          src={poolStatus.watermark}
          alt=""
          aria-hidden
          className="MultiLocationSummaryCards__FlaskWatermark"
        />
        <p className="MultiLocationSummaryCards__Label">Shared pool Available</p>
        <p className={`MultiLocationSummaryCards__Value MultiLocationSummaryCards__Value--${poolStatus.valueTone}`}>
          {formatTests(sharedPoolAvailable)}
        </p>
        <p className={`MultiLocationSummaryCards__Status MultiLocationSummaryCards__Status--${poolStatus.tone}`}>
          {poolStatus.icon}
          {sharedPoolStatusLabel}
        </p>
      </article>

      <article className="MultiLocationSummaryCards__Card MultiLocationSummaryCards__Card--withWatermark">
        <img
          src={lineChartIcon}
          alt=""
          aria-hidden
          className="MultiLocationSummaryCards__ChartWatermark"
        />
        <p className="MultiLocationSummaryCards__Label">
          {isLocationSelected ? "Used by selected location" : "Consumed during this period"}
        </p>
        <p className="MultiLocationSummaryCards__Value">
          {consumedSummaryReady ? consumedTestsValue : <Spin size="small" />}
        </p>
        {consumedSummaryReady && consumedSummary?.detail ? (
          <p className={`MultiLocationSummaryCards__Status MultiLocationSummaryCards__Status--${growthTone}`}>
            {consumedSummary.detailTone === "positive" && <ArrowUpOutlined />}
            {consumedSummary.detailTone === "negative" && <ArrowDownOutlined />}
            {consumedSummary.detail}
          </p>
        ) : consumedSummaryReady && periodLabel && (
          <p className="MultiLocationSummaryCards__Status MultiLocationSummaryCards__Status--muted">
            <CalendarOutlined />
            {periodLabel}
          </p>
        )}
      </article>

      <article className="MultiLocationSummaryCards__Card MultiLocationSummaryCards__Card--withWatermark">
        <img
          src={buildingFillIcon}
          alt=""
          aria-hidden
          className="MultiLocationSummaryCards__LocationsWatermark"
        />
        <p className="MultiLocationSummaryCards__Label">
          {isLocationSelected ? "Selected location" : "Active locations"}
        </p>
        <p className="MultiLocationSummaryCards__Value">
          {isLocationSelected ? selectedLocationLabel : Number(activeLocations).toLocaleString()}
        </p>
        <p className="MultiLocationSummaryCards__Status MultiLocationSummaryCards__Status--muted">
          {isLocationSelected ? "Using shared pool" : activeLocationsStatus}
        </p>
      </article>
    </section>
  );
}
